import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import type { TarifaMarketplace, TipoAnuncio } from '@profitlens/calculo';
import { functions } from '../config/firebase';

/**
 * Ponte com o Mercado Livre.
 *
 * Tudo passa por Cloud Function porque a chave da aplicação é segredo — quem a
 * tem age como o ProfitLens. O navegador nunca fala com a API do Mercado Livre
 * direto.
 */

export interface ResultadoSincronizacao {
  criadas: number;
  atualizadas: number;
  total: number;
}

/** Traz as categorias do Mercado Livre. Só nome e identificador: tarifa não se cadastra. */
export async function sincronizarCategorias(): Promise<ResultadoSincronizacao> {
  const chamar = httpsCallable<Record<string, never>, ResultadoSincronizacao>(
    functions,
    'sincronizarCategoriasML',
  );
  const r = await chamar({});
  return r.data;
}

export type TarifasPorTipo = Partial<Record<TipoAnuncio, TarifaMarketplace>>;

/**
 * Quanto o Mercado Livre cobra por ESTA venda.
 *
 * O preço entra na consulta porque a tarifa depende dele: a mesma categoria
 * cobra percentuais diferentes em faixas diferentes de preço. Consultar sem o
 * preço devolveria um número que só vale por acaso.
 */
export async function consultarTarifa(
  mlCategoriaId: string,
  precoCents: number,
): Promise<TarifasPorTipo> {
  const chamar = httpsCallable<
    { mlCategoriaId: string; precoCents: number },
    { precoCents: number; tarifas: TarifasPorTipo }
  >(functions, 'consultarTarifaML');
  const r = await chamar({ mlCategoriaId, precoCents });
  return r.data.tarifas;
}

/**
 * Consulta a tarifa e mantém o resultado enquanto categoria e preço não mudam.
 *
 * Espera um pouco antes de perguntar porque o preço vem de um campo que a
 * pessoa está digitando: sem isso, "12345" dispararia cinco consultas, quatro
 * delas para preços que nunca existiram.
 *
 * A resposta anterior é descartada assim que os parâmetros mudam. Manter a
 * tarifa velha na tela enquanto a nova não chega mostraria um número que não
 * corresponde ao preço exibido ao lado — e ninguém notaria.
 */
export function useTarifaML(mlCategoriaId: string | null | undefined, precoCents: number | null) {
  const [tarifas, setTarifas] = useState<TarifasPorTipo | null>(null);
  const [consultando, setConsultando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    setTarifas(null);
    setErro(null);
    if (!mlCategoriaId || !precoCents || precoCents <= 0) return;

    let valeAinda = true;
    setConsultando(true);
    const agendado = setTimeout(() => {
      consultarTarifa(mlCategoriaId, precoCents)
        .then((t) => {
          if (valeAinda) setTarifas(t);
        })
        .catch((e: { message?: string }) => {
          if (valeAinda) setErro(e.message ?? 'Não consegui consultar a tarifa.');
        })
        .finally(() => {
          if (valeAinda) setConsultando(false);
        });
    }, 600);

    return () => {
      valeAinda = false;
      clearTimeout(agendado);
    };
  }, [mlCategoriaId, precoCents]);

  return { tarifas, consultando, erro };
}

// ---------- ligação com a conta ----------

export interface StatusConexao {
  conectado: boolean;
  usuarioML?: number;
  apelido?: string | null;
  /** `5_green`, `3_yellow`… traduzir com `reputacaoDoNivelML`. */
  reputacaoNivel?: string | null;
  conectadoEm?: string | null;
  renovadoEm?: string | null;
  expiraEm?: string | null;
}

/**
 * Pede o link de autorização e leva o navegador até lá.
 *
 * O link vem da função, não do navegador, porque ele carrega um `state` que
 * precisa ficar guardado no servidor para ser conferido na volta. Um estado
 * gerado aqui seria um estado que o próprio atacante escolhe.
 */
export async function conectarMercadoLivre(): Promise<void> {
  const chamar = httpsCallable<Record<string, never>, { url: string }>(
    functions,
    'iniciarConexaoML',
  );
  const r = await chamar({});
  window.location.href = r.data.url;
}

export async function statusConexaoML(): Promise<StatusConexao> {
  const chamar = httpsCallable<Record<string, never>, StatusConexao>(functions, 'statusConexaoML');
  return (await chamar({})).data;
}

export async function desconectarML(): Promise<void> {
  const chamar = httpsCallable<Record<string, never>, { desconectado: boolean }>(
    functions,
    'desconectarML',
  );
  await chamar({});
}

/**
 * Diagnóstico da conta ligada: o que o token DELA abre além do que a aplicação
 * já abria. Não grava nada.
 *
 * Existe para responder, com dado e não com suposição, a pergunta que motivou o
 * OAuth: o custo de frete do vendedor sai por API? Com o token de aplicação não
 * sai — todos os endereços de cálculo respondem 404.
 */
export async function sondarConta(): Promise<Record<string, unknown>> {
  const chamar = httpsCallable<Record<string, never>, Record<string, unknown>>(
    functions,
    'sondarContaML',
  );
  return (await chamar({})).data;
}

/**
 * Testa se dá para buscar anúncios DE OUTROS vendedores pelo EAN.
 *
 * É a pendência P1: sem token a busca dá 403, e com o token da aplicação nunca
 * foi testada porque o app foi criado depois. A resposta decide se a pesquisa de
 * mercado roda sozinha ou continua dependendo de digitação.
 */
export async function sondarBusca(ean: string): Promise<Record<string, unknown>> {
  const chamar = httpsCallable<{ ean: string }, Record<string, unknown>>(
    functions,
    'sondarBuscaML',
  );
  return (await chamar({ ean })).data;
}
